/**
 * Shared «Track creation certificate» generator (Кристина п.2) — song-generator,
 * music-generator and voice-cover call it, so the PDF logic lives in ONE place.
 *
 * HONEST proof-of-creation: a client-side PDF — NOT a claim of authorship or a
 * commercial license (the disclaimer says so). Renders through the shared
 * report-pdf.js pipeline (window.__generatePDF), which the calling page MUST load.
 *
 * Два вида полей (чат 658, 30.09.2026 — метка времени и свидетельство
 * разошлись в названии и имени, и человек решил, что документы о разном):
 *   - «Указано владельцем аккаунта» — название песни и автор. Их вписывает сам
 *     человек (подставлены название трека и имя из профиля). Мы за них не
 *     ручаемся и так и пишем.
 *   - «Записано Timbrica» — аккаунт (логин), дата создания трека, номер, файл и
 *     его SHA-256, дата выдачи. Это не правится.
 *
 * Какой ФАЙЛ описан. У одной песни их несколько: обычный MP3 (в него при выдаче
 * пишутся название и логин), MP3 выбранного битрейта, WAV и FLAC из мастера.
 * Сервер помнит отпечаток каждого выданного файла (реестр ai_music_deliveries,
 * App\Services\AiMusic\TrackFiles), и человек выбирает, какой из них описать, —
 * или сверяет файл с устройства: отпечаток считается прямо в браузере, на
 * сервер уходит только он. До 30.09.2026 модуль хэшировал только то, что лежит
 * у нас сейчас: после переименования трека, для WAV и для MP3 320 отпечаток не
 * совпадал со скачанным, а на удалённую песню свидетельства не было вовсе.
 *
 *   window.__trackCertificate({
 *     dataUrl,          // GET /api/ai-music/certificate/{id}/{n} (owner-only)
 *     matchUrl,         // POST /api/ai-music/certificate/match
 *     idLabel,          // e.g. "9001-1"
 *     filename,         // output name WITHOUT .pdf
 *     strings,          // localized labels — the set is what this file reads as s.<key>
 *     toast,            // optional (msg, type) — defaults to window.__toast
 *   }) -> Promise<{ ok:boolean }>  (never rejects)
 *
 *   window.__trackCertificate.byFile({ matchUrl, filename, strings, toast })
 *     — свидетельство по файлу с устройства, когда песни в списке уже нет
 *       (удалена или ушла по сроку хранения).
 */
(function () {
    'use strict';

    function esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    // Без рисовальщика (report-pdf.js → window.__generatePDF) модуль бессилен,
    // и об этом обязана узнать телеметрия, а не только человек: на /voice-cover
    // страница его не грузила, и кнопка «Свидетельство» молчала с 22.08 по
    // 29.09 — увидеть это было нечем, пока не спросили в поддержке.
    function noEngine(where) {
        try {
            if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'track_cert_no_pdf_engine', where: where });
        } catch (e) {}
    }

    function digestHex(buf) {
        if (!buf || !window.crypto || !crypto.subtle) return Promise.resolve('');
        return crypto.subtle.digest('SHA-256', buf).then(function (h) {
            return Array.prototype.map.call(new Uint8Array(h), function (b) {
                return ('0' + b.toString(16)).slice(-2);
            }).join('');
        }).catch(function () { return ''; });
    }

    function utc(iso) {
        if (!iso) return '';
        try { return new Date(iso).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'; } catch (e) { return ''; }
    }

    // Off-screen A4-ish certificate DOM. report-pdf.js clones it into an 800px
    // wrapper and renders UNATTACHED — so NO position:fixed/offset here (that
    // pushes the clone off-canvas → blank PDF). Values via esc() — arbitrary text.
    function buildEl(s, data) {
        var el = document.createElement('div');
        el.style.cssText = 'max-width:680px;margin:0 auto;padding:8px 6px;background:#fff;color:#1a1a2e;font-family:\'Segoe UI\',Roboto,Arial,sans-serif;box-sizing:border-box';
        function rows(list) {
            return list.map(function (r) {
                return '<div style="margin:0 0 12px"><div style="font-size:12px;color:#6b6b80;text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px">' + esc(r[0]) + '</div><div style="font-size:15px;font-weight:600;word-break:break-all">' + esc(r[1]) + '</div></div>';
            }).join('');
        }
        function block(head, list) {
            return '<div style="margin:0 0 18px;padding:14px 16px 4px;border:1px solid #e3e6ef;border-radius:10px">'
                + '<div style="font-size:12px;font-weight:700;color:#2d6cdf;letter-spacing:.04em;margin-bottom:12px">' + esc(head) + '</div>'
                + rows(list) + '</div>';
        }
        var declared = [[s.f_track, data.title || '—'], [s.f_author, data.author || '—']];
        var recorded = [
            [s.f_account, data.owner || '—'], [s.f_date, utc(data.date) || '—'],
            [s.f_id, (s.brand || 'Timbrica') + ' #' + data.id],
            [s.f_file, data.file || '—'], [s.f_hash, data.hash || '—'],
        ];
        if (data.got) recorded.push([s.f_got, utc(data.got)]);
        var lyricsHtml = data.lyrics ? '<div style="margin:22px 0 0"><div style="font-size:12px;color:#6b6b80;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px">' + esc(s.f_lyrics) + '</div><div style="font-size:13px;line-height:1.6;white-space:pre-wrap;color:#333">' + esc(data.lyrics) + '</div></div>' : '';
        el.innerHTML =
            '<div style="border-bottom:3px solid #2d6cdf;padding-bottom:14px;margin-bottom:22px">'
          + '<div style="font-size:12px;font-weight:800;color:#2d6cdf;letter-spacing:.08em;margin-bottom:6px">' + esc(String(s.brand || 'Timbrica').toUpperCase()) + '</div>'
          + '<div style="font-size:22px;font-weight:800;color:#12122a">' + esc(s.title) + '</div></div>'
          + block(s.declared, declared) + block(s.recorded, recorded) + lyricsHtml
          + '<div style="margin:28px 0 0;padding:14px 16px;background:#f4f6fb;border-radius:10px;font-size:12px;line-height:1.6;color:#555">' + esc(s.disclaimer) + '</div>';
        return el;
    }

    // Локальный сертификат: файл существует ТОЛЬКО в браузере (слайдшоу и
    // другие полностью клиентские экспорты) — сервера, который знал бы дату и
    // владельца, нет. Хешируем переданные байты, остальные строки даёт
    // вызывающий. Тот же честный жанр: фиксация факта создания, не лицензия.
    window.__localCertificate = function (opts) {
        opts = opts || {};
        var s = opts.strings || {};
        var toast = opts.toast || (window.__toast || function () {});
        toast(s.making, 'info');
        var bytesP = opts.blob && opts.blob.arrayBuffer
            ? opts.blob.arrayBuffer()
            : Promise.resolve(opts.bytes || null);
        return bytesP.then(digestHex).then(function (hash) {
            if (!window.__generatePDF) { noEngine('local'); toast(s.err, 'error'); return { ok: false }; }
            var rows = (opts.rows || []).concat([[s.f_hash, hash || '—']]);
            var el = document.createElement('div');
            el.style.cssText = 'max-width:680px;margin:0 auto;padding:8px 6px;background:#fff;color:#1a1a2e;font-family:\'Segoe UI\',Roboto,Arial,sans-serif;box-sizing:border-box';
            var rowsHtml = rows.map(function (r) {
                return '<div style="margin:0 0 13px"><div style="font-size:12px;color:#6b6b80;text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px">' + esc(r[0]) + '</div><div style="font-size:15px;font-weight:600;word-break:break-all">' + esc(r[1]) + '</div></div>';
            }).join('');
            el.innerHTML =
                '<div style="border-bottom:3px solid #2d6cdf;padding-bottom:14px;margin-bottom:26px">'
              + '<div style="font-size:12px;font-weight:800;color:#2d6cdf;letter-spacing:.08em;margin-bottom:6px">TIMBRICA</div>'
              + '<div style="font-size:22px;font-weight:800;color:#12122a">' + esc(s.title) + '</div></div>'
              + rowsHtml
              + '<div style="margin:28px 0 0;padding:14px 16px;background:#f4f6fb;border-radius:10px;font-size:12px;line-height:1.6;color:#555">' + esc(s.disclaimer) + '</div>';
            var fn = String(opts.filename || 'certificate').slice(0, 90) + '.pdf';
            return window.__generatePDF({ element: el, filename: fn, orientation: 'portrait', scale: 2, strings: { libError: s.err, genError: s.err } })
                .then(function (r) {
                    toast((r && r.ok) ? s.done : s.err, (r && r.ok) ? 'success' : 'error');
                    return { ok: !!(r && r.ok) };
                });
        }).catch(function () { toast(s.err, 'error'); return { ok: false }; });
    };

    window.__trackCertificate = function (opts) {
        opts = opts || {};
        var s = opts.strings || {};
        var toast = opts.toast || (window.__toast || function () {});
        if (!window.__generatePDF) { noEngine('track'); toast(s.err, 'error'); return Promise.resolve({ ok: false }); }
        if (!opts.dataUrl) { toast(s.err, 'error'); return Promise.resolve({ ok: false }); }

        return getJson(opts.dataUrl).then(function (d) {
            if (!d || !d.ok) { toast(s.err, 'error'); return { ok: false }; }
            return openDialog(opts, s, toast, d);
        }).catch(function () { toast(s.err, 'error'); return { ok: false }; });
    };

    // Свидетельство по файлу: песни в списке уже нет, а файл у человека есть.
    window.__trackCertificate.byFile = function (opts) {
        opts = opts || {};
        var s = opts.strings || {};
        var toast = opts.toast || (window.__toast || function () {});
        if (!window.__generatePDF) { noEngine('by_file'); toast(s.err, 'error'); return Promise.resolve({ ok: false }); }
        return openDialog(opts, s, toast, null);
    };

    // Blob.arrayBuffer() нет в Safari до 14 — там читаем через FileReader.
    function readBuffer(file) {
        if (file.arrayBuffer) return file.arrayBuffer();
        return new Promise(function (ok, fail) {
            var r = new FileReader();
            r.onload = function () { ok(r.result); };
            r.onerror = function () { fail(r.error); };
            r.readAsArrayBuffer(file);
        });
    }

    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? m.getAttribute('content') || '' : '';
    }

    function getJson(url) {
        return fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; });
    }

    function postJson(url, body) {
        return fetch(url, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify(body)
        }).then(function (r) { return r.ok ? r.json() : null; });
    }

    function fmtLabel(s, format) {
        var f = String(format || '');
        var m = /^mp3-(\d+)$/.exec(f);
        if (m) return 'MP3 · ' + String(s.kbps || ':n kbps').replace(':n', m[1]);
        return f.toUpperCase() || '—';
    }

    function localDate(iso) {
        try {
            return new Date(iso).toLocaleDateString(window.__intlLocale && window.__intlLocale(),
                { day: 'numeric', month: 'long', year: 'numeric' });
        } catch (e) { return (iso || '').slice(0, 10); }
    }

    function fileLine(s, f) {
        return (f.filename ? f.filename + ' · ' : '')
            + (f.downloaded_at ? String(s.file_got || '').replace(':date', localDate(f.downloaded_at)) : (s.file_new || ''));
    }

    var styled = false;
    function injectStyle() {
        if (styled) return;
        styled = true;
        var css = ''
            + '.tcert-dlg{margin:auto;padding:0;border:1px solid var(--border,rgba(255,255,255,.14));border-radius:16px;background:var(--bg-secondary,#16162a);color:var(--text-primary,#eef);width:min(560px,calc(100vw - 24px));max-height:calc(100dvh - 24px);overflow:hidden}'
            + '.tcert-dlg::backdrop{background:rgba(0,0,0,.55)}'
            + '.tcert-card{display:flex;flex-direction:column;max-height:calc(100dvh - 26px);min-height:0;margin:0}'
            + '.tcert-body{overflow:auto;min-height:0;padding:20px 20px 8px}'
            + '.tcert-body h2{margin:0 0 6px;font-size:18px;line-height:1.3}'
            + '.tcert-muted{margin:0 0 14px;font-size:13px;line-height:1.5;color:var(--text-secondary,#aab)}'
            + '.tcert-sec{margin:0 0 16px;padding:0;border:0}'
            + '.tcert-sec legend,.tcert-lbl{display:block;margin:0 0 8px;font-size:13px;font-weight:700}'
            + '.tcert-file{display:flex;gap:10px;align-items:flex-start;padding:10px 12px;margin:0 0 8px;border:1px solid var(--border,rgba(255,255,255,.14));border-radius:12px;cursor:pointer;font-size:14px}'
            + '.tcert-file input{margin:3px 0 0;flex:none}'
            + '.tcert-file small{display:block;margin-top:2px;font-size:12px;color:var(--text-secondary,#aab);word-break:break-word}'
            + '.tcert-dev{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-top:4px}'
            + '.tcert-dev-out{font-size:13px;line-height:1.4}'
            + '.tcert-dev-out[hidden]{display:none}'
            + '.tcert-dev-out.is-ok{color:var(--success,#3ecf8e)}'
            + '.tcert-dev-out.is-bad{color:var(--warning,#f5a524)}'
            + '.tcert-in{display:block;width:100%;box-sizing:border-box;margin:0 0 12px;padding:10px 12px;font-size:15px;border-radius:10px;border:1px solid var(--border,rgba(255,255,255,.14));background:var(--bg-primary,#0e0e1c);color:inherit}'
            + '.tcert-actions{display:flex;flex-wrap:wrap;gap:10px;justify-content:flex-end;padding:12px 20px 18px;border-top:1px solid var(--border,rgba(255,255,255,.1))}'
            + '.tcert-btn{display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:0 16px;border-radius:10px;border:1px solid var(--border,rgba(127,127,127,.35));background:transparent;color:inherit;font:inherit;font-size:14px;font-weight:600;cursor:pointer}'
            + '.tcert-btn:hover{border-color:var(--border-hover,rgba(127,127,127,.6))}'
            + '.tcert-btn-sm{min-height:34px;padding:0 12px;font-size:13px}'
            + '.tcert-btn-primary{background:var(--accent-gradient,var(--accent,#2d6cdf));border-color:transparent;color:#fff}'
            + '.tcert-btn-primary:disabled{opacity:.5;cursor:default}';
        var st = document.createElement('style');
        st.textContent = css;
        document.head.appendChild(st);
    }

    // Диалог: какой файл описать, что вписать от себя, «Сформировать PDF».
    // d — данные дубля (null в режиме «по файлу», пока файл не сверен).
    function openDialog(opts, s, toast, d) {
        injectStyle();
        return new Promise(function (resolve) {
            var track = d;                    // данные песни, за которые ручается сервер
            var files = d ? (d.files || []).slice() : [];
            var chosen = files.length ? 0 : -1;
            var done = false;

            var dlg = document.createElement('dialog');
            dlg.className = 'tcert-dlg';
            dlg.setAttribute('aria-labelledby', 'tcertHead');
            dlg.innerHTML =
                '<form method="dialog" class="tcert-card">'
              + '<div class="tcert-body">'
              + '<h2 id="tcertHead">' + esc(s.title) + '</h2>'
              + '<p class="tcert-muted">' + esc(d ? s.dlg_hint : s.by_file_hint) + '</p>'
              + '<fieldset class="tcert-sec"><legend>' + esc(s.pick_file) + '</legend>'
              + '<div data-files></div>'
              + '<div class="tcert-dev"><button type="button" class="tcert-btn tcert-btn-sm" data-dev>' + esc(s.device) + '</button>'
              + '<span class="tcert-dev-out" data-dev-out role="status" hidden></span></div>'
              + '<p class="tcert-muted" style="margin-top:8px">' + esc(s.device_hint) + '</p>'
              + '<input type="file" data-dev-in hidden accept="audio/*,video/mp4,.mp3,.wav,.flac,.m4a,.mp4">'
              + '</fieldset>'
              + '<div class="tcert-sec"><div class="tcert-lbl">' + esc(s.declared) + '</div>'
              + '<label class="tcert-muted" style="display:block;margin:0 0 4px" for="tcertTitle">' + esc(s.f_track) + '</label>'
              + '<input class="tcert-in" id="tcertTitle" data-title maxlength="160" autocomplete="off">'
              + '<label class="tcert-muted" style="display:block;margin:0 0 4px" for="tcertAuthor">' + esc(s.f_author) + '</label>'
              + '<input class="tcert-in" id="tcertAuthor" data-author maxlength="120" autocomplete="name" placeholder="' + esc(s.author_ph) + '">'
              + '</div>'
              + '</div>'
              + '<div class="tcert-actions">'
              + '<button type="button" class="tcert-btn" data-cancel>' + esc(s.cancel) + '</button>'
              + '<button type="submit" class="tcert-btn tcert-btn-primary" data-make>' + esc(s.make) + '</button>'
              + '</div></form>';

            var list = dlg.querySelector('[data-files]');
            var devBtn = dlg.querySelector('[data-dev]');
            var devIn = dlg.querySelector('[data-dev-in]');
            var devOut = dlg.querySelector('[data-dev-out]');
            var titleIn = dlg.querySelector('[data-title]');
            var authorIn = dlg.querySelector('[data-author]');
            var makeBtn = dlg.querySelector('[data-make]');

            function fillTrack() {
                if (!track) return;
                if (!titleIn.value) titleIn.value = track.title || '';
                if (!authorIn.value) authorIn.value = track.author_default || '';
            }

            function renderFiles() {
                list.innerHTML = '';
                files.forEach(function (f, i) {
                    var lab = document.createElement('label');
                    lab.className = 'tcert-file';
                    lab.innerHTML = '<input type="radio" name="tcertFile" value="' + i + '"' + (i === chosen ? ' checked' : '') + '>'
                        + '<span><strong>' + esc(fmtLabel(s, f.format)) + '</strong><small>' + esc(fileLine(s, f)) + '</small></span>';
                    lab.querySelector('input').addEventListener('change', function () { chosen = i; });
                    list.appendChild(lab);
                });
                makeBtn.disabled = chosen < 0 || !track;
            }

            function say(text, cls) {
                devOut.textContent = text || '';
                devOut.className = 'tcert-dev-out' + (cls ? ' ' + cls : '');
                devOut.hidden = !text;
            }

            function finish(res) {
                if (done) return;
                done = true;
                try { dlg.close(); } catch (e) {}
                if (dlg.parentNode) dlg.parentNode.removeChild(dlg);
                resolve(res);
            }

            devBtn.addEventListener('click', function () { devIn.value = ''; devIn.click(); });
            devIn.addEventListener('change', function () {
                var file = devIn.files && devIn.files[0];
                if (!file) return;
                say(s.matching, '');
                readBuffer(file).then(digestHex).then(function (hash) {
                    if (!hash) throw new Error('hash');
                    var body = { sha256: hash };
                    if (track && track.id) { body.id = track.id; body.n = track.n; }
                    return postJson(opts.matchUrl, body);
                }).then(function (r) {
                    if (!r || !r.ok || !r.file) { say(s.match_none, 'is-bad'); return; }
                    if (!track) { track = r; fillTrack(); }
                    var at = -1;
                    files.forEach(function (f, i) { if (f.sha256 === r.file.sha256) at = i; });
                    if (at < 0) { files.push(r.file); at = files.length - 1; }
                    chosen = at;
                    renderFiles();
                    say(String(s.match_ok || '').replace(':file', fmtLabel(s, r.file.format)), 'is-ok');
                }).catch(function () { say(s.err, 'is-bad'); });
            });

            dlg.querySelector('[data-cancel]').addEventListener('click', function () { finish({ ok: false }); });
            dlg.addEventListener('cancel', function () { finish({ ok: false }); });

            dlg.querySelector('form').addEventListener('submit', function (e) {
                e.preventDefault();
                if (chosen < 0 || !track || makeBtn.disabled) return;
                var f = files[chosen];
                makeBtn.disabled = true;
                toast(s.making, 'info');
                // Сервер узнаёт о КАЖДОМ документе: закрепляет файл (теги MP3 больше
                // не меняются, даже после переименования трека) и запоминает его.
                // До 18.10.2026 свидетельство на уже скачанный файл собиралось без
                // единого запроса — и сервер не мог отличить документированный дубль
                // от просто скачанного (чат 592: переименование отпускает только второй).
                var sure = postJson(opts.matchUrl, {
                    sha256: f.sha256, id: track.id, n: track.n, purpose: 'certify'
                }).then(function (r) { return r && r.ok && r.file ? r.file : (f.known ? f : null); });
                sure.then(function (file) {
                    if (!file) { toast(s.err, 'error'); makeBtn.disabled = false; return; }
                    var el = buildEl(s, {
                        title: (titleIn.value || '').trim() || track.title || '',
                        author: (authorIn.value || '').trim(),
                        owner: track.owner || '', date: track.created_at || '',
                        id: opts.idLabel || (track.id + '-' + track.n),
                        file: (file.filename ? file.filename + ' · ' : '') + fmtLabel(s, file.format),
                        hash: file.sha256, got: file.downloaded_at || '', lyrics: track.lyrics || ''
                    });
                    var base = opts.filename || ((titleIn.value || track.title || 'track').trim() + ' — certificate');
                    return window.__generatePDF({ element: el, filename: String(base).slice(0, 90) + '.pdf', orientation: 'portrait', scale: 2, strings: { libError: s.err, genError: s.err } })
                        .then(function (r) {
                            var ok = !!(r && r.ok);
                            toast(ok ? s.done : s.err, ok ? 'success' : 'error');
                            if (ok) finish({ ok: true }); else makeBtn.disabled = false;
                        });
                }).catch(function () { toast(s.err, 'error'); makeBtn.disabled = false; });
            });

            fillTrack();
            renderFiles();
            document.body.appendChild(dlg);
            try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
        });
    }
})();
